import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ListTodo, Plus, CheckCircle2, Lightbulb, ShieldQuestion, Pencil, RotateCcw } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, AcStatus, fmtDate } from './design';

// Follow-up actions (investigation, fixes) with an owner and a due date, and the continuous
// improvement register they feed: incidents, complaints, feedback and audits each can open an
// improvement; it is closed with what changed.

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
let ownersCache = null;
function useOwners() {
  const [owners, setOwners] = useState(ownersCache || []);
  useEffect(() => {
    if (ownersCache) return;
    api.get('/aged-care/attention/owners').then((r) => { ownersCache = r.data || []; setOwners(ownersCache); }).catch(() => {});
  }, []);
  return owners;
}

// The owner picker. An action can belong to someone who is no longer in the owners list (they left,
// or the list has not loaded yet); that person is kept as an option under their name, so opening and
// saving the action never quietly takes it off them. Their id is never shown.
function OwnerSelect({ id, value, onChange, owners, currentName }) {
  const known = !value || owners.some((o) => String(o.id) === String(value));
  return (
    <select id={id} className="input-field !w-auto" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Not assigned</option>
      {!known && <option value={value}>{currentName || 'The current owner'}</option>}
      {owners.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed user'}</option>)}
    </select>
  );
}

export function CiActions({ source, sourceId, improvementId, title = 'Follow-up actions' }) {
  const owners = useOwners();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [f, setF] = useState(null); // a new action being added
  const [edit, setEdit] = useState(null); // { id, title, ownerId, dueDate, ownerName } while one is being changed
  const [done, setDone] = useState(null);
  const load = useCallback(() => {
    const params = improvementId ? { improvement_id: improvementId } : { source, source_id: sourceId };
    setLoadError(null);
    api.get('/aged-care/ci-actions', { params }).then((r) => setRows(r.data || []))
      .catch((e) => { setRows([]); setLoadError(e.response?.data?.message || 'Could not load the actions'); });
  }, [source, sourceId, improvementId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post('/aged-care/ci-actions', { source: improvementId ? 'improvement' : source, sourceId: improvementId ? null : sourceId, improvementId: improvementId || null, ...f }); setF(null); load(); toast.success('Action added'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not add the action'); }
  };
  const saveEdit = async () => {
    if (!edit.title.trim()) return toast.error('Say what is to be done');
    try {
      // Owner and due date are always sent, so clearing either one in the form clears it here too.
      await api.put(`/aged-care/ci-actions/${edit.id}`, { title: edit.title.trim(), ownerId: edit.ownerId || null, dueDate: edit.dueDate || null });
      setEdit(null); load(); toast.success('Action updated');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the action'); }
  };
  const finish = async () => {
    try { await api.post(`/aged-care/ci-actions/${done.id}/done`, { note: done.note }); setDone(null); load(); toast.success('Marked done'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not mark it done'); }
  };
  // Reopening clears the note of what was done, so it asks first.
  const reopen = async (a) => {
    const ok = await confirm(`"${a.title}" counts as open again and the note of what was done is cleared.`, { title: 'Reopen this action?', confirmLabel: 'Reopen' });
    if (!ok) return;
    try { await api.post(`/aged-care/ci-actions/${a.id}/reopen`); load(); toast.success('Action reopened'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not reopen the action'); }
  };
  const today = isoToday();
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-1.5 text-[12.5px]">
      <div className="flex items-center justify-between">
        <p className="font-medium text-slate-800 flex items-center gap-1.5"><ListTodo size={14} /> {title}</p>
        {!f && <button type="button" className="btn-ghost btn-sm" onClick={() => setF({ title: '', ownerId: '', dueDate: '' })}><Plus size={13} /> Add</button>}
      </div>
      {loadError && (
        <p className="flex flex-wrap items-center gap-2 text-red-800" role="alert">
          {loadError}
          <button type="button" className="btn-ghost btn-sm" onClick={load}>Try again</button>
        </p>
      )}
      {!loadError && rows.length === 0 && !f && <p className="text-slate-500">None yet.</p>}
      {rows.map((a) => (edit?.id === a.id ? (
        <div key={a.id} className="flex flex-wrap items-end gap-2 rounded-md bg-slate-50 p-2">
          <Field label="What is to be done" className="flex-1 min-w-[12rem]">{(id) => <input id={id} className="input-field" value={edit.title} onChange={(e) => setEdit((p) => ({ ...p, title: e.target.value }))} />}</Field>
          <Field label="Who">{(id) => <OwnerSelect id={id} value={edit.ownerId} onChange={(v) => setEdit((p) => ({ ...p, ownerId: v }))} owners={owners} currentName={edit.ownerName} />}</Field>
          <Field label="By">{(id) => <input id={id} type="date" className="input-field !w-auto" value={edit.dueDate} onChange={(e) => setEdit((p) => ({ ...p, dueDate: e.target.value }))} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setEdit(null)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" onClick={saveEdit}>Save</button>
        </div>
      ) : (
        <div key={a.id} className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={a.done_at ? 'text-slate-500 line-through' : a.due_date && a.due_date < today ? 'text-red-800' : 'text-slate-700'}>
            {a.title}{a.owner_name ? ` · ${a.owner_name}` : ''}{a.due_date ? ` · by ${fmtDate(a.due_date)}` : ''}
            {a.done_at && a.done_note ? <span className="no-underline"> · {a.done_note}</span> : null}
          </span>
          {a.done_at ? (
            <button type="button" className="btn-ghost btn-sm" onClick={() => reopen(a)}><RotateCcw size={13} /> Reopen</button>
          ) : done?.id === a.id ? (
            <span className="flex flex-wrap items-center gap-1">
              <input aria-label="What was done" className="input-field !w-56 !py-1" value={done.note} onChange={(e) => setDone((p) => ({ ...p, note: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finish(); } }} placeholder="What was done" />
              <button type="button" className="btn-ghost btn-sm" onClick={() => setDone(null)}>Cancel</button>
              <button type="button" className="btn-primary btn-sm" onClick={finish}>Save</button>
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <button type="button" className="btn-ghost btn-sm" aria-label={`Edit "${a.title}"`}
                onClick={() => { setDone(null); setEdit({ id: a.id, title: a.title || '', ownerId: a.owner_id || '', ownerName: a.owner_name || '', dueDate: a.due_date || '' }); }}>
                <Pencil size={13} /> Edit
              </button>
              <button type="button" className="btn-ghost btn-sm" onClick={() => { setEdit(null); setDone({ id: a.id, note: '' }); }}><CheckCircle2 size={13} /> Mark done</button>
            </span>
          )}
        </div>
      )))}
      {f && (
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <Field label="What is to be done" className="flex-1">{(id) => <input id={id} className="input-field" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} />}</Field>
          <Field label="Who">{(id) => <OwnerSelect id={id} value={f.ownerId} onChange={(v) => setF((p) => ({ ...p, ownerId: v }))} owners={owners} />}</Field>
          <Field label="By">{(id) => <input id={id} type="date" className="input-field !w-auto" value={f.dueDate} onChange={(e) => setF((p) => ({ ...p, dueDate: e.target.value }))} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setF(null)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" onClick={add}>Add</button>
        </div>
      )}
    </div>
  );
}

const IMPROVEMENTS_CHANGED = 'aged-care:improvements-changed';

// "Add to the improvement register" from an incident or a complaint.
// One record makes one improvement. The button checks the register when the record opens and turns
// into a plain "Added" once there is one, so it cannot be pressed twice into two copies.
export function ToImprovement({ source, sourceId, suggested }) {
  const [busy, setBusy] = useState(false);
  // The record it was added for, so moving the drawer to another record offers the button again.
  const [addedFor, setAddedFor] = useState(null);
  useEffect(() => {
    let live = true;
    api.get('/aged-care/improvements')
      .then((r) => { if (live && (r.data || []).some((i) => i.source === source && String(i.source_id) === String(sourceId))) setAddedFor(sourceId); })
      .catch(() => {}); // only a convenience: if the check fails the button is still offered
    return () => { live = false; };
  }, [source, sourceId]);
  const added = addedFor != null && String(addedFor) === String(sourceId);
  const add = async () => {
    setBusy(true);
    try {
      await api.post('/aged-care/improvements', { title: suggested, source, sourceId });
      setAddedFor(sourceId);
      // The register at the foot of the Complaints screen reloads, so the new entry is there when
      // the message sends people to look for it.
      window.dispatchEvent(new Event(IMPROVEMENTS_CHANGED));
      toast.success('Added to the improvement register. You will find it on the Complaints screen, under Improvement register.');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not add it to the improvement register'); } finally { setBusy(false); }
  };
  if (added) {
    return (
      <button type="button" className="btn-ghost btn-sm" disabled title="On the Complaints screen, under Improvement register">
        <CheckCircle2 size={13} /> Added to the improvement register
      </button>
    );
  }
  return <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={add}><Lightbulb size={13} /> {busy ? 'Adding…' : 'Add to the improvement register'}</button>;
}

const SOURCE = { sirs: 'Incident', complaint: 'Complaint', feedback: 'Feedback', audit: 'Audit', staff: 'Staff', other: 'Other' };
// Incidents and complaints make their improvement from their own record, so those two sources are
// never offered by hand; an improvement that came from one keeps it.
const LINKED = ['sirs', 'complaint'];
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', done: 'Closed' };

// The add and edit form share these fields.
function ImprovementForm({ f, setF, onCancel, onSave }) {
  const linked = LINKED.includes(f.source);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2">
      <FormRow cols={2}>
        <Field label="What is to be improved">{(id) => <input id={id} className="input-field" value={f.title} onChange={set('title')} />}</Field>
        <Field label="From" hint={linked ? `Made from ${f.source === 'sirs' ? 'an incident' : 'a complaint'}, so it stays linked to it.` : undefined}>
          {(id) => (
            <select id={id} className="input-field" value={f.source} onChange={set('source')} disabled={linked}>
              {Object.entries(SOURCE).filter(([k]) => !LINKED.includes(k) || k === f.source).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </Field>
      </FormRow>
      <Field label="Details">{(id) => <textarea id={id} rows={2} className="input-field" value={f.description} onChange={set('description')} />}</Field>
      <FormRow cols={2}>
        <Field label="Quality Standard (optional)">{(id) => <input id={id} className="input-field" value={f.qualityStandard} onChange={set('qualityStandard')} placeholder="For example, Standard 2: The organisation" />}</Field>
        <Field label="Target date">{(id) => <input id={id} type="date" className="input-field" value={f.targetDate} onChange={set('targetDate')} />}</Field>
      </FormRow>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary btn-sm" onClick={onSave}>Save</button>
      </div>
    </div>
  );
}

// Rendered at the foot of the Complaints screen. The section carries the id and the name the
// "added to the improvement register" message points people to.
export function ImprovementRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [f, setF] = useState(null);
  const [edit, setEdit] = useState(null); // the improvement being changed, in the form's shape
  const [open, setOpen] = useState(null);
  const [close, setClose] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get('/aged-care/improvements').then((r) => setRows(r.data || []))
      .catch((e) => { setRows([]); setLoadError(e.response?.data?.message || 'Could not load the improvement register'); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener(IMPROVEMENTS_CHANGED, load);
    return () => window.removeEventListener(IMPROVEMENTS_CHANGED, load);
  }, [load]);
  const add = async () => {
    if (!f.title.trim()) return toast.error('Say what is to be improved');
    try { await api.post('/aged-care/improvements', f); setF(null); load(); toast.success('Added to the improvement register'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not add the improvement'); }
  };
  const saveEdit = async () => {
    if (!edit.title.trim()) return toast.error('Say what is to be improved');
    const body = { title: edit.title.trim(), description: edit.description, qualityStandard: edit.qualityStandard, targetDate: edit.targetDate || null };
    if (!LINKED.includes(edit.source)) body.source = edit.source;
    try { await api.put(`/aged-care/improvements/${edit.id}`, body); setEdit(null); load(); toast.success('Improvement updated'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the improvement'); }
  };
  const setStatus = async (r, status, outcome) => {
    try {
      await api.put(`/aged-care/improvements/${r.id}`, { status, outcome });
      setClose(null); load();
      toast.success(status === 'done' ? 'Improvement closed' : status === 'open' ? 'Improvement reopened' : 'Improvement started');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the improvement'); }
  };
  const startEdit = (r) => {
    setClose(null); setF(null);
    setEdit({ id: r.id, title: r.title || '', source: r.source || 'other', description: r.description || '', qualityStandard: r.quality_standard || '', targetDate: r.target_date || '' });
  };
  return (
    <section id="improvement-register" aria-label="Improvement register" className="mt-5 scroll-mt-20">
      <Panel title="Improvement register" icon={Lightbulb} bodyClass="px-4 py-3 space-y-2 text-[13px]"
        actions={!f && !edit && <button type="button" className="btn-secondary btn-sm" onClick={() => setF({ title: '', source: 'feedback', description: '', qualityStandard: '', targetDate: '' })}><Plus size={13} /> Add</button>}>
        <p className="text-[12px] text-slate-500">The continuous improvement register: what you are changing because of incidents, complaints, feedback and audits, with the actions and what changed. Evidence for the Strengthened Quality Standards.</p>
        {loading && <p className="text-slate-500" aria-busy="true">Loading the register…</p>}
        {loadError && (
          <p className="flex flex-wrap items-center gap-2 text-red-800" role="alert">
            {loadError}
            <button type="button" className="btn-secondary btn-sm" onClick={load}>Try again</button>
          </p>
        )}
        {!loading && !loadError && rows.length === 0 && !f && <p className="text-slate-500">Nothing recorded yet.</p>}
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="py-2">
              {edit?.id === r.id ? (
                <ImprovementForm f={edit} setF={setEdit} onCancel={() => setEdit(null)} onSave={saveEdit} />
              ) : (
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <button type="button" className="text-left min-w-0" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
                    <span className="font-medium text-slate-800">{r.title}</span>
                    <span className="block text-[12px] text-slate-500">{SOURCE[r.source] || 'Other'} · opened {fmtDate(r.opened_on)}{r.target_date ? ` · target ${fmtDate(r.target_date)}` : ''}
                      {r.open_actions ? ` · ${r.open_actions} open action${r.open_actions === 1 ? '' : 's'}` : ''}{r.quality_standard ? ` · ${r.quality_standard}` : ''}</span>
                    {r.status === 'done' && r.outcome && <span className="block text-[12px] text-emerald-700">Closed {fmtDate(r.closed_on)}: {r.outcome}</span>}
                  </button>
                  <span className="flex flex-wrap items-center gap-1">
                    <AcStatus status={r.status === 'done' ? 'closed' : r.status} label={STATUS_LABEL[r.status]} />
                    <button type="button" className="btn-ghost btn-sm" aria-label={`Edit "${r.title}"`} onClick={() => startEdit(r)}><Pencil size={13} /> Edit</button>
                    {r.status === 'open' && <button type="button" className="btn-ghost btn-sm" onClick={() => setStatus(r, 'in_progress')}>Start</button>}
                    {r.status !== 'done' && <button type="button" className="btn-secondary btn-sm" onClick={() => { setEdit(null); setClose({ id: r.id, outcome: '' }); }}>Close</button>}
                    {r.status === 'done' && <button type="button" className="btn-ghost btn-sm" onClick={() => setStatus(r, 'open')}><RotateCcw size={13} /> Reopen</button>}
                  </span>
                </div>
              )}
              {close?.id === r.id && (
                <div className="flex flex-wrap items-end gap-2 mt-1.5">
                  <Field label="What changed" className="flex-1">{(id) => <input id={id} className="input-field" value={close.outcome} onChange={(e) => setClose((p) => ({ ...p, outcome: e.target.value }))} />}</Field>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setClose(null)}>Cancel</button>
                  <button type="button" className="btn-primary btn-sm" onClick={() => setStatus(r, 'done', close.outcome)}>Close it</button>
                </div>
              )}
              {open === r.id && edit?.id !== r.id && <div className="mt-2">{r.description && <p className="text-[12.5px] text-slate-600 mb-2">{r.description}</p>}<CiActions improvementId={r.id} title="Actions" /></div>}
            </li>
          ))}
        </ul>
        {f && <ImprovementForm f={f} setF={setF} onCancel={() => setF(null)} onSave={add} />}
      </Panel>
    </section>
  );
}

// SIRS triage: the questions that decide reportable and priority; a person confirms.
const TYPES = [
  ['unreasonable_force', 'Unreasonable use of force'], ['unlawful_sexual_contact', 'Unlawful sexual contact or inappropriate sexual conduct'],
  ['psychological_abuse', 'Psychological or emotional abuse'], ['unexpected_death', 'Unexpected death'],
  ['stealing_financial_coercion', 'Stealing or financial coercion by a staff member'], ['neglect', 'Neglect'],
  ['restrictive_practices', 'Inappropriate use of restrictive practices'], ['unexplained_absence', 'Unexplained absence from care'],
  ['not_listed', 'None of these'],
];
export function SirsTriage({ inc, onDone }) {
  const t0 = inc.triage || {};
  const [a, setA] = useState({ type: t0.type || '', harm: t0.harm ?? null, police: t0.police ?? null });
  const [result, setResult] = useState(null);
  const yn = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
  useEffect(() => {
    if (!a.type) { setResult(null); return; }
    api.post(`/aged-care/sirs/${inc.id}/triage`, { ...a, confirm: false }).then((r) => setResult(r.data.result)).catch(() => {});
  }, [a, inc.id]);
  const confirm = async () => {
    try { await api.post(`/aged-care/sirs/${inc.id}/triage`, { ...a, confirm: true }); toast.success('Triage recorded'); onDone(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); }
  };
  const ask = (key, label) => (
    <Field label={label}>
      {(id) => (
        <select id={id} className="input-field !w-auto" value={yn(a[key])} onChange={(e) => setA((p) => ({ ...p, [key]: e.target.value === '' ? null : e.target.value === 'yes' }))}>
          <option value="">Choose</option><option value="yes">Yes</option><option value="no">No</option>
        </select>
      )}
    </Field>
  );
  const decided = result && result.reportable != null && (!result.reportable || result.priority != null);
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2 text-[12.5px]">
      <p className="font-medium text-slate-800 flex items-center gap-1.5"><ShieldQuestion size={14} /> Triage {inc.triaged_at ? <span className="font-normal text-slate-500">(recorded {fmtDate(inc.triaged_at)})</span> : null}</p>
      <Field label="What kind of incident is it (alleged or suspected counts)?">
        {(id) => <select id={id} className="input-field" value={a.type} onChange={(e) => setA((p) => ({ ...p, type: e.target.value }))}><option value="">Choose</option>{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
      </Field>
      {a.type && a.type !== 'not_listed' && (
        <div className="flex flex-wrap gap-3">
          {ask('harm', 'Did it cause, or could it reasonably have caused, injury or discomfort needing medical or psychological treatment?')}
          {ask('police', 'Are there reasonable grounds to report it to police?')}
        </div>
      )}
      {result && (
        <p className={result.priority === 1 ? 'text-red-800' : 'text-slate-700'}>
          {result.reportable === false ? 'Not reportable under SIRS.' : result.priority ? `Reportable, priority ${result.priority}: notify within ${result.priority === 1 ? '24 hours' : '30 days'} of becoming aware.` : 'Reportable; answer the questions to set the priority.'}
          {result.reasons.length ? <span className="block text-slate-500">{result.reasons.join('; ')}.</span> : null}
        </p>
      )}
      {decided && <div className="flex justify-end"><button type="button" className="btn-primary btn-sm" onClick={confirm}>Confirm</button></div>}
    </div>
  );
}
